import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["bar", "toggle", "form", "input", "suggestions", "options", "all", "status"]

  connect() {
    this.sequence = (this.sequence || 0) + 1
    this.composing = false
    this.hideSuggestions()
    this.barTarget.hidden = this.element.dataset.publicSearchOpen !== "true"
    this.syncToggle()
  }

  disconnect() {
    this.cancel()
  }

  toggle(event) {
    event.preventDefault()
    this.barTarget.hidden = !this.barTarget.hidden
    this.syncToggle()
    if (!this.barTarget.hidden) this.inputTarget.focus()
    else {
      this.cancel()
      this.hideSuggestions()
    }
  }

  close() {
    this.cancel()
    this.hideSuggestions()
    this.barTarget.hidden = true
    this.syncToggle()
    this.toggleTarget.focus()
  }

  syncToggle() {
    this.toggleTarget.setAttribute("aria-expanded", String(!this.barTarget.hidden))
  }

  cancel() {
    clearTimeout(this.timer)
    this.sequence += 1
    if (this.request) this.request.abort()
  }

  changed() {
    this.cancel()
    const query = this.inputTarget.value.trim()
    if (this.composing || query.length < 2 || query.length > 200) {
      this.hideSuggestions()
      return
    }
    this.prepareSuggestions(query)
    const sequence = this.sequence
    this.timer = setTimeout(() => this.load(query, sequence), 275)
  }

  compositionStart() {
    this.composing = true
    this.cancel()
    this.resetSelection()
    this.optionsTarget.setAttribute("inert", "")
  }

  compositionEnd() {
    this.composing = false
    this.changed()
  }

  async load(query, sequence) {
    if (sequence !== this.sequence) return
    this.request = new AbortController()
    try {
      const url = new URL(this.element.dataset.publicSearchSuggestionsUrl, window.location.origin)
      url.searchParams.set("q", query)
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!response.ok || response.redirected) throw new Error("Suggestions unavailable")
      const html = await response.text()
      if (sequence !== this.sequence) return
      this.optionsTarget.innerHTML = html
      this.optionsTarget.removeAttribute("inert")
      this.optionsTarget.scrollTop = 0
      const count = this.options.length
      const found = count === 1 ? "Atrasts 1 ieteikums." : `Atrasti ${count} ieteikumi.`
      this.statusTarget.textContent = count ? `${found} Izmanto bultiņas, lai izvēlētos.` : "Nekas netika atrasts. Pamēģini citu vārdu."
      this.reservePanelHeight()
    } catch (error) {
      if (sequence === this.sequence && error.name !== "AbortError") {
        this.optionsTarget.replaceChildren()
        this.optionsTarget.removeAttribute("inert")
        this.statusTarget.textContent = "Ieteikumus neizdevās ielādēt. Nospied “Meklēt”, lai mēģinātu pilno meklēšanu."
        this.reservePanelHeight()
      }
    } finally {
      if (sequence === this.sequence) this.inputTarget.removeAttribute("aria-busy")
    }
  }

  get options() {
    return Array.from(this.optionsTarget.querySelectorAll('[role="option"]'))
  }

  prepareSuggestions(query) {
    this.resetSelection()
    this.suggestionsTarget.hidden = false
    this.optionsTarget.setAttribute("inert", "")
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.inputTarget.setAttribute("aria-busy", "true")
    const all = new URL(this.formTarget.action)
    all.searchParams.set("q", query)
    this.allTarget.href = all.href
    this.allTarget.hidden = false
    this.statusTarget.textContent = "Meklē ieteikumus…"
    this.reservePanelHeight()
  }

  reservePanelHeight() {
    const height = this.suggestionsTarget.getBoundingClientRect().height
    this.suggestionsTarget.style.setProperty("--public-search-panel-min-height", `${height}px`)
  }

  resetSelection() {
    this.active = -1
    this.options.forEach(option => option.setAttribute("aria-selected", "false"))
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  keydown(event) {
    if (event.isComposing) return
    if (event.key === "Escape") {
      event.preventDefault()
      if (!this.suggestionsTarget.hidden) {
        this.cancel()
        this.hideSuggestions()
      } else this.close()
    } else if (["ArrowDown", "ArrowUp"].includes(event.key) && !this.suggestionsTarget.hidden && !this.optionsTarget.hasAttribute("inert") && this.options.length) {
      event.preventDefault()
      const delta = event.key === "ArrowDown" ? 1 : -1
      this.active = this.active < 0 ? (delta > 0 ? 0 : this.options.length - 1) : (this.active + delta + this.options.length) % this.options.length
      this.options.forEach((option, index) => option.setAttribute("aria-selected", String(index === this.active)))
      const option = this.options[this.active]
      this.inputTarget.setAttribute("aria-activedescendant", option.id)
      option.scrollIntoView({ block: "nearest" })
    } else if (event.key === "Enter" && this.active >= 0 && !this.suggestionsTarget.hidden && !this.optionsTarget.hasAttribute("inert")) {
      event.preventDefault()
      window.location.assign(this.options[this.active].href)
    } else if (event.key === "Tab") {
      this.cancel()
      this.hideSuggestions()
    }
  }

  submit(event) {
    // Explicit submit buttons always search all results; active options only affect Enter in the input.
    const query = this.inputTarget.value.trim()
    if (query.length < 2 || query.length > 200) {
      event.preventDefault()
      this.cancel()
      this.hideSuggestions()
      this.suggestionsTarget.hidden = false
      this.allTarget.hidden = true
      this.inputTarget.setAttribute("aria-expanded", "true")
      this.statusTarget.textContent = "Ievadi no 2 līdz 200 rakstzīmēm."
      this.inputTarget.focus()
      return
    }
    this.cancel()
  }

  outside(event) {
    if (!this.barTarget.contains(event.target)) {
      this.cancel()
      this.hideSuggestions()
    }
  }

  hideSuggestions() {
    this.resetSelection()
    this.statusTarget.textContent = ""
    this.suggestionsTarget.hidden = true
    this.suggestionsTarget.style.removeProperty("--public-search-panel-min-height")
    this.optionsTarget.replaceChildren()
    this.optionsTarget.removeAttribute("inert")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.inputTarget.removeAttribute("aria-busy")
  }
}
